import { RootState } from 'duck/store'
import { flow, isEmpty } from 'lodash'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getDeal } from 'scenes/Dashboard/data/deals/selectors'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { makeStyles } from 'tss-react/mui'

import { Paper, TablePagination, Theme } from '@mui/material'

import { updateCurrentPage, updateSearchCount } from '../duck/actions'
import { getDealManagerFilters, getOrderedDeals } from '../duck/selectors'
import { DealCard } from './DealCard'

export const DealStack = React.memo(() => {
  const { classes } = useStyles()
  const { deals, limit, currentPage } = useDealStackData()
  const { onChangePage, onChangeElementsPerPage } = useDealStackHandlers()
  const noAvailableDeals = isEmpty(deals)

  // TODO: Wait for the real count from back
  const hardcodedCount = limit > deals.length ? deals.length : limit * (currentPage + 1) + 1
  const handlePageChange = useCallback(
    (event: any, page: number) => {
      // Due to a bug of material UI, tables fire this callback on re-renders
      if (Boolean(event)) {
        onChangePage(page)
      }
    },
    [onChangePage]
  )

  const handleElementsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeElementsPerPage(Number(event.target.value))
    },
    [onChangeElementsPerPage]
  )

  const paginationComponent = (
    <Paper elevation={2} className={classes.pagination}>
      <span></span>
      <TablePagination
        key={currentPage}
        count={hardcodedCount}
        rowsPerPageOptions={[10, 30, 50]}
        rowsPerPage={limit}
        page={currentPage}
        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleElementsPerPageChange}
        classes={{
          select: classes.select,
        }}
      />
    </Paper>
  )

  const body = (
    <div className={classes.dealList}>
      {deals.map((deal: Deal) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const newDeal = useSelector((state: RootState) => getDeal(state, deal.id))
        return (
          newDeal && (
            <DealCard
              className={classes.deal}
              key={newDeal.id}
              orderedDeal={newDeal}
              analysis={deal.analysis}
              corporate={deal.corporate}
            />
          )
        )
      })}
    </div>
  )

  return (
    <>
      {paginationComponent}
      {!noAvailableDeals && body}
      {paginationComponent}
    </>
  )
})

DealStack.displayName = 'DealStack'

function useDealStackData() {
  const deals = useSelector(getOrderedDeals)
  const { currentPage, limit } = useSelector(getDealManagerFilters)
  return { currentPage, limit, deals }
}

function useDealStackHandlers() {
  const dispatch = useDispatch()
  return {
    onChangePage: useCallback(flow(updateCurrentPage, dispatch), [dispatch]),
    onChangeElementsPerPage: useCallback(flow(updateSearchCount, dispatch), [dispatch]),
  }
}

const useStyles = makeStyles()(({ spacing }: Theme) => {
  return {
    select: {
      fontWeight: 'normal',
    },
    dealList: {
      flex: '1 1 auto',
    },
    deal: {
      marginBottom: spacing(2),
    },
    pagination: {
      display: 'flex',
      flex: '0 0 auto',
      justifyContent: 'space-between',
      marginBottom: spacing(2),
    },
    expandToggle: {
      margin: spacing(),
      fontWeight: 'normal',
    },
    table: {
      tableLayout: 'fixed',
    },
  }
})
